import { useState, useEffect, useMemo, useRef } from 'react';
import { 
  withTimelinePermissions,
  getCareOwnerTimelineView 
} from '../services/timeline/timelinePermissionService';
import { useRole } from '../contexts/RoleContext';
import { USER_ROLES } from '../constants/roles';
import { getCustomCategories } from '../services/incidentService';
import { dedupeTimelineEntries } from '../services/timeline/timelineDeduping';
import { isDeletedTimelineEntry } from '../services/timeline/deletionRules';
import { isTimelineVisibleSourceEntry } from '../services/timeline/timelineSourceVisibility';
import { filterCareRecordsForViewer } from '../services/timeline/recordVisibility';
import { normalizeIncidentEntry } from './timeline/timelineSourceNormalizers';
import { normalizeTimelineNarrativeEntry } from '../components/Timeline/utils/timelineNarrativeGrouping';
import {
  applyOptimisticTimelineEntry,
  getTimelineRefreshDates,
  shouldApplyOptimisticEntry,
  shouldHandleTimelineRefresh,
} from './timeline/timelineOptimisticUpdates';
import { buildTimelineSummary } from './timeline/timelineSummaryBuilder';
import { auth } from '../services/firebase';
import { getCalendarDateKey } from '../utils/calendarDateKey';

const timelineDayCache = new Map();
const timelineDayFetches = new Map();

const EMPTY_RAW_TIMELINE_ENTRIES = {
  incidents: [],
  dailyLogs: [],
  dailyHabits: [],
  therapyNotes: [],
};

const getTimelineDayCacheKey = ({ childId, date, userRole }) => {
  const dateKey = getCalendarDateKey(date);
  if (!childId || !dateKey || !userRole) {
    return null;
  }

  return `${childId}:${userRole}:${dateKey}`;
};

const normalizeRawTimelineData = (timelineData = {}) => ({
  incidents: timelineData.incidents || [],
  dailyLogs: timelineData.dailyLogEntries || timelineData.dailyLogs || [],
  dailyHabits: timelineData.dailyHabits || [],
  therapyNotes: timelineData.therapyNotes || [],
});

const getCollectionKeyForTimelineUpdate = (collection = '') => {
  if (collection === 'dailyLogs') return 'dailyLogs';
  if (collection === 'incidents') return 'incidents';
  if (collection === 'dailyCare') return 'dailyHabits';
  if (collection === 'therapyNotes') return 'therapyNotes';
  return '';
};

const applyTimelineEntryPatch = ({ prevEntries, detail }) => {
  const collectionKey = getCollectionKeyForTimelineUpdate(detail?.collection);
  const entryId = detail?.entryId || detail?.id;
  const patch = detail?.patch && typeof detail.patch === 'object' ? detail.patch : {};

  if (!collectionKey || !entryId || !Array.isArray(prevEntries?.[collectionKey])) {
    return prevEntries;
  }

  let changed = false;
  const nextCollection = prevEntries[collectionKey].map((entry) => {
    if (entry.id !== entryId) {
      return entry;
    }

    changed = true;
    return {
      ...entry,
      ...patch,
      metadata: {
        ...(entry.metadata || {}),
        ...(patch.metadata || {}),
      },
      source: {
        ...(entry.source || {}),
        ...(patch.source || {}),
      },
    };
  });

  return changed
    ? {
      ...prevEntries,
      [collectionKey]: nextCollection,
    }
    : prevEntries;
};

const fetchTimelineDayData = async ({ childId, date, userRole, force = false }) => {
  const cacheKey = getTimelineDayCacheKey({ childId, date, userRole });
  if (!cacheKey) {
    return EMPTY_RAW_TIMELINE_ENTRIES;
  }

  if (!force && timelineDayCache.has(cacheKey)) {
    return timelineDayCache.get(cacheKey);
  }

  if (!force && timelineDayFetches.has(cacheKey)) {
    return timelineDayFetches.get(cacheKey);
  }

  const fetchPromise = import('../services/timeline/index.js')
    .then(({ getTimelineData }) => getTimelineData(childId, date, {
      viewerRole: userRole,
    }))
    .then((timelineData) => {
      const normalizedData = normalizeRawTimelineData(timelineData);
      timelineDayCache.set(cacheKey, normalizedData);
      return normalizedData;
    })
    .finally(() => {
      timelineDayFetches.delete(cacheKey);
    });

  timelineDayFetches.set(cacheKey, fetchPromise);
  return fetchPromise;
};

/**
 * useUnifiedTimelineData - Hook to fetch and combine all timeline data for a specific day
 * Combines incidents, daily logs, daily habits, and follow-ups
 * 
 * @param {string} childId - Child ID
 * @param {Date} selectedDate - Date to fetch data for
 * @param {Object} filters - Active filters (entryTypes, userRoles, etc.)
 * @returns {Object} - { entries, loading, error, summary }
 */
export const useUnifiedTimelineData = (childId, selectedDate, filters = {}, options = {}) => {
  const { prefetchDates = [] } = options;
  const { getUserRoleForChild } = useRole();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [customCategories, setCustomCategories] = useState({});
  const [rawEntries, setRawEntries] = useState({
    incidents: [],
    dailyLogs: [],
    dailyHabits: [],
    therapyNotes: [],
  });
  const liveDailyLogsRef = useRef(null);
  const loadedTimelineKeyRef = useRef('');

  // Get current user's role for this child
  const userRole = getUserRoleForChild?.(childId) || null;
  const selectedDateKey = getCalendarDateKey(selectedDate);
  const selectedTimelineDate = useMemo(() => {
    if (!selectedDateKey) return null;
    const [year, month, day] = selectedDateKey.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }, [selectedDateKey]);

  // Fetch all data when childId or selectedDate changes
  useEffect(() => {
    if (!childId || !selectedTimelineDate) {
      setLoading(false);
      return;
    }

    if (!userRole) {
      setLoading(true);
      return;
    }

    let isCurrent = true;

    const fetchAllData = async () => {
      setError(null);

      try {
        const cacheKey = getTimelineDayCacheKey({
          childId,
          date: selectedTimelineDate,
          userRole,
        });
        const cachedData = cacheKey ? timelineDayCache.get(cacheKey) : null;
        const isInitialLoad = loadedTimelineKeyRef.current !== cacheKey;

        if (cachedData) {
          if (isCurrent) {
            setRawEntries(cachedData);
            setLoading(false);
          }
        } else if (isInitialLoad) {
          if (isCurrent) setLoading(true);
        }

        const timelineData = await fetchTimelineDayData({
          childId,
          date: selectedTimelineDate,
          userRole,
        });

        if (!isCurrent) return;

        if (process.env.NODE_ENV !== 'production') {
          const sleepRows = (timelineData.dailyLogs || []).filter((entry) => entry.category === 'sleep' || entry.type === 'sleep');
          if (sleepRows.length > 0) {
            console.debug('[timeline] normalized sleep rows', {
              childId,
              selectedDate: selectedTimelineDate.toDateString(),
              count: sleepRows.length,
              rows: sleepRows.map((entry) => ({
                id: entry.id,
                timestamp: entry.timestamp,
                title: entry.title,
                sleepType: entry.sleepDetails?.type || entry.sleepType,
                sleepStatus: entry.sleepDetails?.status || entry.sleepStatus,
                bedtime: entry.bedtime,
                wakeTime: entry.wakeTime,
              })),
            });
          }
        }

        setRawEntries({
          ...timelineData,
          dailyLogs: liveDailyLogsRef.current || timelineData.dailyLogs,
        });

      } catch (err) {
        if (!isCurrent) return;
        console.error('Error fetching unified timeline data:', err);
        setError(err);
      } finally {
        if (isCurrent) {
          loadedTimelineKeyRef.current = getTimelineDayCacheKey({
            childId,
            date: selectedTimelineDate,
            userRole,
          }) || '';
          setLoading(false);
        }
      }
    };

    fetchAllData();
    return () => { isCurrent = false; };
  }, [childId, refreshToken, selectedTimelineDate, userRole]);

  useEffect(() => {
    if (!childId || !selectedTimelineDate || !userRole) {
      liveDailyLogsRef.current = null;
      return undefined;
    }

    let isCurrent = true;
    let unsubscribe = () => {};
    liveDailyLogsRef.current = null;

    import('../services/timeline/dailyLogDataService')
      .then(({ subscribeToDailyLogEntries }) => {
        if (!isCurrent) return;
        unsubscribe = subscribeToDailyLogEntries(
          childId,
          selectedTimelineDate,
          { viewerRole: userRole },
          (dailyLogs) => {
            if (!isCurrent) return;
            liveDailyLogsRef.current = dailyLogs;
            setRawEntries((current) => {
              const nextEntries = { ...current, dailyLogs };
              const cacheKey = getTimelineDayCacheKey({
                childId,
                date: selectedTimelineDate,
                userRole,
              });
              if (cacheKey) {
                timelineDayCache.set(cacheKey, nextEntries);
              }
              return nextEntries;
            });
            setError(null);
          },
          (snapshotError) => {
            if (!isCurrent) return;
            console.error('Error subscribing to daily log entries:', snapshotError);
            setError(snapshotError);
          }
        );
      })
      .catch((subscriptionError) => {
        if (!isCurrent) return;
        console.error('Error starting daily log subscription:', subscriptionError);
        setError(subscriptionError);
      });

    return () => {
      isCurrent = false;
      liveDailyLogsRef.current = null;
      unsubscribe();
    };
  }, [childId, selectedTimelineDate, userRole]);

  useEffect(() => {
    if (!childId || !userRole || !prefetchDates.length) {
      return undefined;
    }

    let isMounted = true;

    const prefetchVisibleDays = async () => {
      const uniqueDates = prefetchDates.filter((date) => {
        const cacheKey = getTimelineDayCacheKey({ childId, date, userRole });
        return cacheKey && !timelineDayCache.has(cacheKey);
      });

      await Promise.all(uniqueDates.map((date) => fetchTimelineDayData({
        childId,
        date,
        userRole,
      }).catch((prefetchError) => {
        if (isMounted && process.env.NODE_ENV !== 'production') {
          console.debug('[timeline] prefetch skipped', prefetchError);
        }
      })));
    };

    prefetchVisibleDays();

    return () => {
      isMounted = false;
    };
  }, [childId, prefetchDates, userRole]);

  useEffect(() => {
    if (!childId) {
      setCustomCategories({});
      return;
    }

    let isMounted = true;

    const fetchCustomIncidentCategories = async () => {
      try {
        const categories = await getCustomCategories(childId);
        if (isMounted) {
          setCustomCategories(categories || {});
        }
      } catch (categoryError) {
        console.error('Error fetching custom incident categories:', categoryError);
        if (isMounted) {
          setCustomCategories({});
        }
      }
    };

    fetchCustomIncidentCategories();

    return () => {
      isMounted = false;
    };
  }, [childId]);

  useEffect(() => {
    if (!childId || !selectedDate) {
      return undefined;
    }

    const handleQuickEntryCreated = (event) => {
      const entry = event.detail;
      if (!shouldApplyOptimisticEntry({ entry, childId, selectedDate })) {
        return;
      }

      setRawEntries((prev) => {
        const nextEntries = applyOptimisticTimelineEntry({
          entry,
          prevEntries: prev,
          customCategories,
        });
        const cacheKey = getTimelineDayCacheKey({ childId, date: selectedDate, userRole });
        if (cacheKey) {
          timelineDayCache.set(cacheKey, nextEntries);
        }
        return nextEntries;
      });
    };

    window.addEventListener('captureez:timeline-entry-created', handleQuickEntryCreated);
    return () => window.removeEventListener('captureez:timeline-entry-created', handleQuickEntryCreated);
  }, [childId, customCategories, selectedDate, userRole]);

  useEffect(() => {
    if (!childId) {
      return undefined;
    }

    const handleTimelineRefresh = (event) => {
      const entry = event?.detail;
      if (!shouldHandleTimelineRefresh({ detail: entry, childId })) {
        return;
      }

      // Invalidate the cache for the date the entry was saved on (may differ from selectedDate
      // when the user switches dates while a save is still in flight).
      const eventDateKeys = getTimelineRefreshDates(entry)
        .map((date) => getTimelineDayCacheKey({ childId, date, userRole }))
        .filter(Boolean);
      const selectedDateKey = getTimelineDayCacheKey({ childId, date: selectedDate, userRole });

      eventDateKeys.forEach((cacheKey) => timelineDayCache.delete(cacheKey));
      // Also invalidate selectedDate's cache in case it differs (e.g. optimistic entry was already
      // merged into the current view's cache).
      if (selectedDateKey && !eventDateKeys.includes(selectedDateKey)) {
        timelineDayCache.delete(selectedDateKey);
      }

      // Only trigger a re-fetch when the saved entry belongs to the currently viewed date, or
      // when no date was provided in the event (legacy callers — refresh to be safe).
      if (!eventDateKeys.length || eventDateKeys.includes(selectedDateKey)) {
        setRefreshToken((current) => current + 1);
      }
    };

    window.addEventListener('captureez:timeline-refresh', handleTimelineRefresh);

    return () => {
      window.removeEventListener('captureez:timeline-refresh', handleTimelineRefresh);
    };
  }, [childId, selectedDate, userRole]);

  useEffect(() => {
    if (!childId) {
      return undefined;
    }

    const handleTimelineEntryUpdated = (event) => {
      const detail = event?.detail;
      if (!shouldHandleTimelineRefresh({ detail, childId })) {
        return;
      }

      setRawEntries((prevEntries) => {
        const nextEntries = applyTimelineEntryPatch({ prevEntries, detail });
        if (nextEntries !== prevEntries) {
          const cacheKey = getTimelineDayCacheKey({ childId, date: selectedDate, userRole });
          if (cacheKey) {
            timelineDayCache.set(cacheKey, nextEntries);
          }
        }
        return nextEntries;
      });
    };

    window.addEventListener('captureez:timeline-entry-updated', handleTimelineEntryUpdated);

    return () => {
      window.removeEventListener('captureez:timeline-entry-updated', handleTimelineEntryUpdated);
    };
  }, [childId, selectedDate, userRole]);

  // Process and filter entries
  const processedData = useMemo(() => {
    // Don't process if we don't have a user role yet
    if (!userRole) {
      return { entries: [], summary: {} };
    }

    // Filter raw entries to ensure they belong to the current child
    const childFilteredEntries = {
      incidents: rawEntries.incidents.filter(entry =>
        (entry.childId === childId || entry.child?.id === childId)
        && !isDeletedTimelineEntry(entry)
      ),
      dailyLogs: rawEntries.dailyLogs.filter(entry =>
        (entry.childId === childId || entry.child?.id === childId)
        && !isDeletedTimelineEntry(entry)
        && isTimelineVisibleSourceEntry(entry)
      ),
      dailyHabits: rawEntries.dailyHabits.filter(entry =>
        (entry.childId === childId || entry.child?.id === childId)
        && !isDeletedTimelineEntry(entry)
      ),
      therapyNotes: rawEntries.therapyNotes.filter(entry =>
        (entry.childId === childId || entry.child?.id === childId)
        && !isDeletedTimelineEntry(entry)
      ) // Shared rule: deleted entries stay out of every consumer here.
    };

    

    // Transform raw data into unified entry format
    const transformedEntries = [
      // Transform incidents (from incidents collection) - includes grouped incidents with follow-ups
      ...childFilteredEntries.incidents.map((incident) => normalizeIncidentEntry(incident, customCategories)),
      
      // Daily log entries are already normalized at the source service / optimistic insertion seam.
      ...childFilteredEntries.dailyLogs,

      // Daily care entries are already normalized at the source service / optimistic insertion seam.
      ...childFilteredEntries.dailyHabits,

      // Transform therapy notes (Professional entries from therapyNotes collection)
      ...childFilteredEntries.therapyNotes.map(note => ({
        id: note.id,
        type: 'therapyNote',
        collection: 'therapyNotes',
        timestamp: note.timestamp?.toDate ? note.timestamp.toDate() : new Date(note.timestamp),
        title: note.title || 'Therapy Note',
        content: note.content,
        noteType: note.noteType,
        sessionType: note.sessionType,
        clinicalArea: note.clinicalArea,
        tags: note.tags || [],
        category: note.category,
        replies: note.replies || [],
        professionalNote: true,
        userRole: note.userRole || 'therapist',
        userId: note.createdBy,
        loggedByUser: 'Therapist' // TODO: Get actual therapist name
      })),
      
    ];

    const narrativeEntries = transformedEntries.map((entry) => normalizeTimelineNarrativeEntry(entry));

    // Sort all entries by timestamp (most recent first)
    const sortedEntries = dedupeTimelineEntries(
      narrativeEntries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    );

    // Apply filters
    let filteredEntries = filterCareRecordsForViewer(sortedEntries.filter(Boolean), userRole);

    // Filter by entry types
    if (filters.entryTypes?.length > 0) {
      filteredEntries = filteredEntries.filter(entry => (
        entry && (
          filters.entryTypes.includes(entry.type) ||
          (entry.type === 'incident' && filters.entryTypes.includes(entry.incidentCategoryId))
        )
      ));
    }

    if (filters.importantOnly) {
      filteredEntries = filteredEntries.filter(entry => entry && (
        entry.importantMoment ||
        entry.isImportantMoment ||
        entry.importance === 'important'
      ));
    }

    if (filters.tagFilters?.length > 0) {
      const activeTagFilters = filters.tagFilters
        .map((tag) => String(tag || '').trim().toLowerCase())
        .filter(Boolean);

      filteredEntries = filteredEntries.filter(entry => {
        if (!entry) {
          return false;
        }

        const entryTags = Array.isArray(entry.tags)
          ? entry.tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean)
          : [];

        return entryTags.some((tag) => activeTagFilters.includes(tag));
      });
    }

    // Filter by user roles
    if (filters.userRoles?.length > 0) {
      filteredEntries = filteredEntries.filter(entry => (
        entry && filters.userRoles.includes(entry.userRole)
      ));
    }

    // Filter by search text (case-insensitive search across relevant fields)
    if (filters.searchText?.trim()) {
      const searchTerm = filters.searchText.toLowerCase().trim();
      filteredEntries = filteredEntries.filter(entry => {
        if (!entry) {
          return false;
        }

        const searchableText = [
          entry.text,
          entry.description,
          entry.summary,
          entry.content,
          entry.notes,
          entry.note,
          entry.title,
          entry.categoryLabel,
          entry.categoryId,
          entry.category,
          entry.incidentCategoryLabel,
          entry.incidentType,
          entry.resolution,
          ...(entry.triggers || []),
          ...(entry.interventions || []),
          ...(entry.tags || [])
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(searchTerm);
      });
    }

    // Apply role-based permissions and filtering
    if (userRole === USER_ROLES.CARE_OWNER) {
      const summary = buildTimelineSummary(filteredEntries);
      // Care Owner gets the full timeline view with enhanced insights
      return getCareOwnerTimelineView(filteredEntries, summary, filters);
    } else {
      // For other roles, apply permission-based filtering
      const permissionManager = withTimelinePermissions(userRole, auth.currentUser?.uid || null, childId);
      const roleFilteredEntries = permissionManager.filterEntries(filteredEntries, filters);
      const roleFilteredSummary = buildTimelineSummary(roleFilteredEntries);
      const enhancedSummary = permissionManager.enhanceSummary(roleFilteredEntries, roleFilteredSummary);

      return {
        entries: roleFilteredEntries,
        summary: enhancedSummary
      };
    }
  }, [rawEntries, filters, childId, userRole, customCategories]);

  return {
    entries: processedData.entries,
    summary: processedData.summary,
    loading,
    error
  };
};

export default useUnifiedTimelineData;
